import i18n from '../../../i18n';
import { Trans } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { cn } from '../../../lib/utils';

function itemKey(item) {
  return String(item.checklistItemId ?? item.id);
}

function ChecklistStatusBadge({ status }) {
  const isDone = status === 'Completed' || status === 'Not Applicable';
  const isInProgress = status === 'In Progress';
  return (
    <span
      className={cn(
        'inline-block shrink-0 self-start px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide',
        isDone
          ? 'bg-emerald-100 text-emerald-800'
          : isInProgress
            ? 'bg-[var(--color-accent)]/12 text-[var(--color-accent)]'
            : 'bg-gray-100 text-gray-600'
      )}
    >
      {status === 'Not Applicable'
        ? i18n.t('fsm.notApplicable')
        : status === 'Completed'
          ? i18n.t('status.completed')
          : status === 'In Progress'
            ? i18n.t('fsm.statusInProgress')
            : i18n.t('status.pending')}
    </span>
  );
}

function StatusCheck({ id, checked, disabled, label, onChange }) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex items-center gap-2 py-1 text-xs font-semibold select-none',
        checked ? 'text-[var(--color-primary)]' : 'text-gray-700',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="h-4 w-4 shrink-0 accent-[var(--color-primary)]"
      />
      {label}
    </label>
  );
}

export function EditableChecklistItems({
  items,
  remarks,
  normalizeStatus,
  disabled = false,
  idPrefix,
  onToggleStatus,
  onSelectAllCompleted
}) {
  const selectAllRef = useRef(null);
  const allCompleted =
    items.length > 0 &&
    items.every((item) => normalizeStatus(remarks[itemKey(item)]) === 'Completed');
  const someCompleted = items.some(
    (item) => normalizeStatus(remarks[itemKey(item)]) === 'Completed'
  );

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someCompleted && !allCompleted;
    }
  }, [someCompleted, allCompleted]);

  return (
    <div className="flex flex-col gap-4">
      <label
        htmlFor={`${idPrefix}-select-all-completed`}
        className={cn(
          'inline-flex items-center gap-2 py-1 text-xs font-semibold select-none',
          allCompleted ? 'text-[var(--color-primary)]' : 'text-gray-700',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <input
          id={`${idPrefix}-select-all-completed`}
          ref={selectAllRef}
          type="checkbox"
          checked={allCompleted}
          disabled={disabled}
          onChange={(e) => onSelectAllCompleted(e.target.checked)}
          className="h-4 w-4 shrink-0 accent-[var(--color-primary)]"
        />
        <Trans i18nKey="fsm.selectAllAsCompleted" />
      </label>

      {items.map((item, idx) => {
        const key = itemKey(item);
        const status = normalizeStatus(remarks[key]);
        return (
          <div
            key={key}
            className="border border-gray-100 rounded-xl p-3 sm:p-4 flex flex-col gap-3 bg-gray-50/30"
          >
            <p className="text-xs font-bold text-foreground min-w-0">
              <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
              {item.whatToCheck}
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 items-start">
              <ChecklistStatusBadge status={status} />
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:ml-auto">
                <StatusCheck
                  id={`${idPrefix}-${key}-completed`}
                  checked={status === 'Completed'}
                  disabled={disabled}
                  label={i18n.t('status.completed')}
                  onChange={(e) =>
                    onToggleStatus(key, e.target.checked ? 'Completed' : 'Pending')
                  }
                />
                <StatusCheck
                  id={`${idPrefix}-${key}-na`}
                  checked={status === 'Not Applicable'}
                  disabled={disabled}
                  label={i18n.t('fsm.notApplicable')}
                  onChange={(e) =>
                    onToggleStatus(key, e.target.checked ? 'Not Applicable' : 'Pending')
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { ChecklistStatusBadge };
